// src/portal/portalMapTokens.js
//
// THE BRIDGE. MapLibre paint expressions take colour STRINGS; they cannot read
// a Tailwind class and they cannot read a CSS custom property. So the token
// layer has to be resolved to strings once, here, and handed to the style.
//
// ── WHY THIS IS A FUNCTION AND NOT A MODULE-LEVEL CONSTANT ─────────────────
//
// A constant would be evaluated at import time, which on a cold load can be
// before the stylesheet has applied, and in jsdom is always before — there is
// no stylesheet at all. Resolving at DRAW time means the colours come from
// whatever theme is in force when the layer is added, which is also what makes
// a future theme switch a re-draw rather than a reload.
//
// ── THE FALLBACKS ARE THE SHIPPING VALUES, AND THEY ARE NOT DECORATION ─────
//
// If a variable is missing — stylesheet failed, token renamed, jsdom — a naive
// bridge hands MapLibre an empty string. MapLibre does not ignore that: it
// throws on an invalid paint value and the layer does not render. A map that
// silently loses the submitter's geometry is a worse failure than a map wearing
// last season's palette, so every lookup has a literal behind it.
//
// THAT MAKES A UNIT TEST OF THESE VALUES AMBIGUOUS BY CONSTRUCTION: in jsdom
// the fallbacks are what you measure, and a completely broken bridge would give
// the same answer. The test that settles it is the one that DEFINES the
// variables on documentElement and asserts the output moves —
// portalMapTokens.test.js — plus the assertion against the built stylesheet.

/**
 * The decided values, as they ship. Three of these are fixed by decision and
 * must not drift: `other`, `untagged` and `markup` were chosen against measured
 * constraints the mock was not drawn with in view — four meanings to separate
 * on one surface, and a collision that already shipped once, where `other` and
 * untagged collapsed to the same grey and a submitter reading a fully grey map
 * would have believed they had tagged everything `other`.
 */
export const GEOMETRY_FALLBACK = Object.freeze({
  pad:       "rgb(45,212,191)",   // #2DD4BF — the mock
  road:      "rgb(255,168,61)",   // #FFA83D — the mock
  other:     "rgb(163,163,163)",  // #a3a3a3 — KEPT
  untagged:  "rgb(226,232,240)",  // #e2e8f0 — KEPT
  markup:    "rgb(244,114,182)",  // #f472b6 — KEPT
  halo:      "rgb(248,250,252)",  // #f8fafc
  backdrop:  "rgb(12,14,16)",     // #0C0E10
});

/** token name in src/tokens.css for each role above. */
const TOKEN = Object.freeze({
  pad: "--geom-pad", road: "--geom-road", other: "--geom-other",
  untagged: "--geom-untagged", markup: "--geom-markup",
  halo: "--geom-halo", backdrop: "--map-backdrop",
});

/**
 * "45 212 191" -> "rgb(45,212,191)".
 *
 * COMMAS, NOT THE SPACE-SEPARATED FORM the variable is stored in. The channels
 * are stored space-separated because that is what Tailwind's
 * `rgb(<color> / <alpha-value>)` needs; MapLibre parses colours with its own
 * parser, and the comma form is the one every version of it accepts.
 */
function toRgb(raw) {
  const parts = String(raw).trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length !== 3) return null;
  if (!parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255)) return null;
  return `rgb(${parts.join(",")})`;
}

/**
 * Resolve the geometry palette from the token layer, falling back per-role.
 *
 * @returns {{pad:string,road:string,other:string,untagged:string,markup:string,halo:string,backdrop:string}}
 */
export function geometryColours() {
  let styles = null;
  try {
    styles = getComputedStyle(document.documentElement);
  } catch {
    return GEOMETRY_FALLBACK;
  }
  if (!styles) return GEOMETRY_FALLBACK;

  const out = {};
  for (const [role, name] of Object.entries(TOKEN)) {
    let resolved = null;
    try { resolved = toRgb(styles.getPropertyValue(name)); } catch { resolved = null; }
    // PER-ROLE, not all-or-nothing. One renamed token should cost one colour,
    // not the whole palette.
    out[role] = resolved ?? GEOMETRY_FALLBACK[role];
  }
  return Object.freeze(out);
}
